import apiClient from './api';
import { DeliverySchedule, DeliveryHistory, ApiResponse, DeliverySettings, Subscription, DeliveryStatus } from '../types/api';
import subscriptionService from './subscriptionService';

// Mock delivery data for development
const MOCK_DELIVERIES = [
  // Mock deliveries for mock-1 (active flower premium)
  {
    id: 'delivery-mock-1-1',
    subscriptionId: 'mock-1',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'scheduled' as DeliveryStatus,
    amount: 599,
    timeSlot: '9:00 AM - 12:00 PM'
  },
  {
    id: 'delivery-mock-1-2',
    subscriptionId: 'mock-1',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'scheduled' as DeliveryStatus,
    amount: 599,
    timeSlot: '9:00 AM - 12:00 PM'
  },
  {
    id: 'delivery-mock-1-past',
    subscriptionId: 'mock-1',
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'delivered' as DeliveryStatus,
    amount: 599,
    timeSlot: '9:00 AM - 12:00 PM'
  },
  // Mock deliveries for mock-2 (active fruit standard)
  {
    id: 'delivery-mock-2-1',
    subscriptionId: 'mock-2',
    date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'scheduled' as DeliveryStatus,
    amount: 399,
    timeSlot: '2:00 PM - 5:00 PM'
  },
  {
    id: 'delivery-mock-2-2',
    subscriptionId: 'mock-2',
    date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'scheduled' as DeliveryStatus,
    amount: 399,
    timeSlot: '2:00 PM - 5:00 PM'
  },
  {
    id: 'delivery-mock-2-past',
    subscriptionId: 'mock-2',
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'delivered' as DeliveryStatus,
    amount: 399,
    timeSlot: '2:00 PM - 5:00 PM'
  },
  // Mock deliveries for mock-3 (cancelled flower standard)
  {
    id: 'delivery-mock-3-past',
    subscriptionId: 'mock-3',
    date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'delivered' as DeliveryStatus,
    amount: 399,
    timeSlot: '9:00 AM - 12:00 PM'
  },
  // Mock deliveries for mock-5 (paused flower standard)
  {
    id: 'delivery-mock-5-past',
    subscriptionId: 'mock-5',
    date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'delivered' as DeliveryStatus,
    amount: 399,
    timeSlot: '9:00 AM - 12:00 PM'
  }
];

// Enable mock data in development
const USE_MOCK_DATA = import.meta.env.DEV && window.location.search.includes('mock=true');

export const deliveryService = {
  // Get a delivery by id
  getDeliveryById: async (deliveryId: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/deliveries/${deliveryId}`);
    return response.data.data;
  },

  /**
   * Get delivery history for the current user
   */
  getDeliveryHistory: async (): Promise<DeliveryHistory[]> => {
    // Get active subscription first
    const subscriptions = await subscriptionService.getAllSubscriptions();
    const activeSubscription = subscriptions.find((sub: any) => sub.status === 'active');
    
    if (!activeSubscription?.id) {
      return [];
    }
    
    // Use subscription-specific endpoint and map to match DeliveryHistory format
    const pastDeliveries = await deliveryService.getPastDeliveries(activeSubscription.id);
    
    // Transform to DeliveryHistory format (generate random ID if needed)
    return pastDeliveries.map(delivery => ({
      id: (delivery as any).id || `history-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      date: delivery.date,
      status: delivery.status,
      address: activeSubscription.address || { 
        id: '',
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    }));
  },
  
  /**
   * Get all deliveries for the current user from all subscriptions
   * @param includeSubscriptions Whether to include subscription details in the response
   * @param statusFilter Optional filter for delivery status
   */
  getAllUserDeliveries: async (
    includeSubscriptions: boolean = false,
    statusFilter?: DeliveryStatus
  ): Promise<DeliveryHistory[]> => {
    try {
      let url = '/user/deliveries';
      const params = new URLSearchParams();
      
      if (includeSubscriptions) {
        params.append('include_subscriptions', 'true');
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log(`Fetching all user deliveries from: ${url}`);
      const response = await apiClient.get<ApiResponse<DeliveryHistory[] | { deliveries: DeliveryHistory[], subscriptions: any[] }>>(url);
      
      if (!response.data.success) {
        console.error('API reported error:', response.data.message);
        return [];
      }
      
      // Handle both formats - array or object with deliveries property
      const responseData = response.data.data;
      if (!responseData) {
        return [];
      }
      
      if (Array.isArray(responseData)) {
        return responseData;
      } else if (responseData.deliveries && Array.isArray(responseData.deliveries)) {
        return responseData.deliveries;
      }
      
      console.error('Unexpected response format:', responseData);
      return [];
    } catch (error) {
      console.error('Error fetching all user deliveries:', error);
      return [];
    }
  },
  
  /**
   * Schedule a delivery
   */
  scheduleDelivery: async (
    date: string, 
    timeSlots: string | string[], 
    subscriptionId: string
  ): Promise<DeliverySchedule | DeliverySchedule[]> => {
    const response = await apiClient.post<ApiResponse<DeliverySchedule | DeliverySchedule[]>>(`/subscriptions/${subscriptionId}/deliveries/schedule`, {
      date,
      timeSlots
    });
    return response.data.data;
  },
  
  /**
   * Create a delivery
   */
  createDelivery: async (
    date: string,
    timeSlot: string,
    subscriptionId: string
  ): Promise<DeliverySchedule> => {
    const response = await apiClient.post<ApiResponse<DeliverySchedule>>(`/subscriptions/${subscriptionId}/deliveries/create`, {
      date,
      timeSlot
    });
    return response.data.data;
  },
  
  /**
   * Skip a delivery
   */
  skipDelivery: async (
    deliveryId: string,
    subscriptionId: string,
    date?: string
  ): Promise<DeliverySchedule> => {
    // If we have a valid deliveryId, use the regular endpoint
    if (deliveryId && deliveryId.trim()) {
      const response = await apiClient.post<ApiResponse<DeliverySchedule>>(`/subscriptions/${subscriptionId}/deliveries/skip`, {
        deliveryId
      });
      return response.data.data;
    } 
    // If we don't have a deliveryId but have a date, use the date-based endpoint
    else if (date) {
      console.log(`Using date-based skip for delivery on ${date}`);
      const response = await apiClient.post<ApiResponse<DeliverySchedule>>(`/subscriptions/${subscriptionId}/deliveries/skip-by-date`, {
        date
      });
      return response.data.data;
    }
    // If we have neither, throw an error
    else {
      throw new Error('Either deliveryId or date must be provided to skip a delivery');
    }
  },
  
  /**
   * Get upcoming deliveries
   * @param existingSubscriptions Optional existing subscriptions data
   * @param forceRefresh Force a fresh request bypassing potential caching
   */
  getUpcomingDeliveries: async (existingSubscriptions?: any[], forceRefresh: boolean = false): Promise<DeliverySchedule[]> => {
    // Use provided subscriptions if available, otherwise fetch them
    const subscriptions = existingSubscriptions || await subscriptionService.getAllSubscriptions(forceRefresh);
    const activeSubscription = subscriptions.find((sub: any) => sub.status === 'active');
    
    // If there's an active subscription, use the subscription-specific endpoint
    if (activeSubscription?.id) {
      return await deliveryService.getSubscriptionDeliveries(activeSubscription.id);
    }
    
    // No active subscription, return empty array
    return [];
  },
  
  /**
   * Get delivery settings
   */
  getDeliverySettings: async (subscriptionId: string): Promise<DeliverySettings> => {
    const response = await apiClient.get<ApiResponse<DeliverySettings>>(`/subscriptions/${subscriptionId}/deliveries/settings`);
    return response.data.data;
  },
  
  /**
   * Update delivery settings
   */
  updateDeliverySettings: async (
    subscriptionId: string,
    day_of_week: number,
    time_slots: string[]
  ): Promise<DeliverySettings> => {
    try {
      const response = await apiClient.post<ApiResponse<DeliverySettings>>(
        `/subscriptions/${subscriptionId}/deliveries/settings`, 
        {
          day_of_week,
          time_slots
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating delivery settings:', error);
      throw error;
    }
  },
  
    /**
   * Get upcoming deliveries for a specific subscription
   */
  getSubscriptionDeliveries: async (subscriptionId: string): Promise<DeliverySchedule[]> => {
    // If using mock data, return mock upcoming deliveries
    if (USE_MOCK_DATA) {
      console.log('DeliveryService: Using mock data for upcoming deliveries');
      const mockDeliveries = MOCK_DELIVERIES.filter(delivery => 
        delivery.subscriptionId === subscriptionId && 
        delivery.status === 'scheduled'
      );
      console.log(`DeliveryService: Found ${mockDeliveries.length} mock upcoming deliveries for subscription ${subscriptionId}`);
      return mockDeliveries;
    }

    try {
      const response = await apiClient.get<ApiResponse<DeliverySchedule[]>>(`/subscriptions/${subscriptionId}/deliveries/upcoming`);
      return response.data.data;
    } catch (error: any) {
      // If no deliveries found for this subscription, return empty array
      if (error.response?.data?.error === "No deliveries found") {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get past deliveries for a specific subscription
   */
  getPastDeliveries: async (subscriptionId: string): Promise<DeliverySchedule[]> => {
    // If using mock data, return mock past deliveries
    if (USE_MOCK_DATA) {
      console.log('DeliveryService: Using mock data for past deliveries');
      const mockDeliveries = MOCK_DELIVERIES.filter(delivery => 
        delivery.subscriptionId === subscriptionId && 
        delivery.status === 'delivered'
      );
      console.log(`DeliveryService: Found ${mockDeliveries.length} mock past deliveries for subscription ${subscriptionId}`);
      return mockDeliveries;
    }

    try {
      const response = await apiClient.get<ApiResponse<DeliverySchedule[]>>(`/subscriptions/${subscriptionId}/deliveries/history`);
      return response.data.data;
    } catch (error: any) {
      // If no deliveries found for this subscription, return empty array
      if (error.response?.data?.error === "No deliveries found") {
        return [];
      }
      throw error;
    }
  }
};

export default deliveryService; 